import { z } from "zod";
export const RatingFieldSchema = z.object({
    id: z.string(),
    type: z.literal("rating"),
    label: z.string(),
    required: z.boolean().optional(),
    // `label` is the visible point (the digit "1".."5"); `ariaLabel` is what a screen
    // reader announces for that point ("Disagree".."Agree"). The scale's visible end
    // labels are the first/last options' ariaLabel, so the whole scale semantic lives
    // in the data, not the component (ADR-023 accessibility annotations).
    options: z.array(z.object({
        value: z.number(),
        label: z.string(),
        ariaLabel: z.string().optional(),
    })),
});
// A rating rendered as a pictogram (smiley-face) scale — same shape and numeric
// options as `rating` (so DSS values stay explicit, e.g. 1/3/5), but the kiosk
// renders the options as faces with word captions instead of numbers. Used for
// the child/teen age brackets (ADR-023).
export const RatingPictogramFieldSchema = z.object({
    id: z.string(),
    type: z.literal("rating-pictogram"),
    label: z.string(),
    required: z.boolean().optional(),
    options: z.array(z.object({ value: z.number(), label: z.string() })),
});
export const TextareaFieldSchema = z.object({
    id: z.string(),
    type: z.literal("textarea"),
    label: z.string(),
    required: z.boolean().optional(),
    maxLength: z.number().optional(),
});
// A single yes/no checkbox — e.g. a marketing consent or "contact me" opt-in.
// Always optional in practice (an unchecked box means "no"); `required` exists
// for parity with the other field types but authoring rarely sets it.
export const CheckboxFieldSchema = z.object({
    id: z.string(),
    type: z.literal("checkbox"),
    label: z.string(),
    required: z.boolean().optional(),
});
const FormFieldSchema = z.discriminatedUnion("type", [
    RatingFieldSchema,
    RatingPictogramFieldSchema,
    TextareaFieldSchema,
    CheckboxFieldSchema,
]);
export const FormSchema = z.object({
    id: z.string(),
    fields: z.array(FormFieldSchema),
});
// A program-question slot — a placeholder the server fills, before serving, with
// the program's question loaded from the program-question store for the context's
// program (one question, chosen among that program's candidates). It embeds no
// questions; the content lives in its own store, so it never reaches the kiosk.
// Part of the authoring contract — the shape an admin/builder edits, distinct from
// the resolved `Form` above that the kiosk receives — only because the
// admin/builder surface authors it.
const ProgramQuestionSlotSchema = z.object({
    type: z.literal("programQuestionSlot"),
    // Which field type the resolved program question renders as: adult forms use
    // "rating", child/teen use "rating-pictogram". Absent → "rating". The schema
    // owns the scale (the bracket only picks the form); mirrors the Form.io builder's
    // properties.fieldType marker (formioMapping) so the two round-trip.
    fieldType: z.enum(["rating", "rating-pictogram"]).optional(),
});
const FormDefinitionFieldSchema = z.discriminatedUnion("type", [
    RatingFieldSchema,
    RatingPictogramFieldSchema,
    TextareaFieldSchema,
    CheckboxFieldSchema,
    ProgramQuestionSlotSchema,
]);
export const FormDefinitionSchema = z.object({
    id: z.string(),
    fields: z.array(FormDefinitionFieldSchema),
});
// The body of an update — the definition minus its id, which the URL owns.
export const UpdateFormDefinitionSchema = FormDefinitionSchema.omit({
    id: true,
});
// A form as the admin form list reports it: enough to render the list without the
// client knowing anything about which forms exist — which is why no id of a
// specific form appears anywhere in shared. `id` is a plain string so a row the
// code has never heard of still comes back; surfacing it is the whole point.
//
// Labels are on the wire for the same reason field and question labels are — naming
// a form is the domain's job, not the renderer's.
export const FormSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    // Forms sharing a group are variants of one instrument (the age-bracket client
    // forms); a form of its own is its own group. The key is opaque — group by it,
    // show groupName.
    group: z.string(),
    groupName: z.string(),
});
export const FormsResponseSchema = z.object({
    forms: FormSummarySchema.array(),
});
